import { useState, useEffect } from 'react'
import { apiUrl, authFetch } from '../../lib/api'

interface SavedDesign { id: string; name: string; created_at: string }

const FORMATS = [
  { id: 'react-jsx', label: 'React (JSX)', ext: '.jsx' },
  { id: 'react-tsx', label: 'React (TypeScript)', ext: '.tsx' },
  { id: 'vue', label: 'Vue (SFC)', ext: '.vue' },
  { id: 'html', label: 'HTML', ext: '.html' },
  { id: 'python-jinja', label: 'Python (Jinja2)', ext: '.html' },
  { id: 'svelte', label: 'Svelte', ext: '.svelte' },
  { id: 'angular', label: 'Angular', ext: '.ts' },
]

const TYPES = [
  { id: 'component', label: 'Component', desc: 'Reusable piece' },
  { id: 'page', label: 'Page', desc: 'Full page/route' },
  { id: 'append', label: 'Append', desc: 'Add to existing file' },
]

export function UseInProjectModal({ html, onClose }: { html?: string; onClose: () => void }) {
  const [designs, setDesigns] = useState<SavedDesign[]>([])
  const [selectedDesign, setSelectedDesign] = useState<string | null>(null)
  const [format, setFormat] = useState('react-jsx')
  const [type, setType] = useState('component')
  const [fileName, setFileName] = useState('MyComponent.jsx')
  const [targetFolder, setTargetFolder] = useState('')
  const [converting, setConverting] = useState(false)
  const [result, setResult] = useState<{ ok: boolean; path?: string; error?: string } | null>(null)

  useEffect(() => {
    authFetch(apiUrl('/api/designs')).then(r => r.json()).then(d => setDesigns(Array.isArray(d) ? d : [])).catch(() => {})
  }, [])

  // Auto-update file extension when format changes
  useEffect(() => {
    const fmt = FORMATS.find(f => f.id === format)
    if (fmt) {
      const baseName = fileName.replace(/\.[^.]+$/, '')
      setFileName(baseName + fmt.ext)
    }
  }, [format])

  const convert = async () => {
    setConverting(true)
    setResult(null)
    try {
      const res = await authFetch(apiUrl('/api/designs/convert'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          html: html ?? undefined,
          designId: !html ? selectedDesign : undefined,
          format,
          type,
          fileName,
          targetFolder: targetFolder || undefined,
        }),
      })
      const data = await res.json()
      if (data.ok) {
        setResult({ ok: true, path: data.path })
        window.dispatchEvent(new CustomEvent('pulse:files-changed'))
      } else {
        setResult({ ok: false, error: data.error })
      }
    } catch (e: any) {
      setResult({ ok: false, error: e.message })
    } finally { setConverting(false) }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1001, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: 460, maxWidth: '90vw', maxHeight: '80vh', background: 'var(--bg-1)', border: '1px solid var(--border-2)', borderRadius: 14, padding: 24, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 16px 48px rgba(0,0,0,0.5)' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, color: 'var(--text-0)', marginBottom: 16 }}>📂 Use in Project</div>

        <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Source selection (only if no html passed directly) */}
          {!html && (
            <div>
              <label style={labelStyle}>Select Design</label>
              <select value={selectedDesign ?? ''} onChange={e => setSelectedDesign(e.target.value)} style={inputStyle}>
                <option value="">Choose a saved design...</option>
                {designs.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
          )}

          {/* Format */}
          <div>
            <label style={labelStyle}>Convert To</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 4 }}>
              {FORMATS.map(f => (
                <button key={f.id} onClick={() => setFormat(f.id)} style={{
                  padding: '6px 8px', borderRadius: 6, fontSize: 10, fontFamily: 'var(--font-mono)', textAlign: 'left',
                  border: format === f.id ? '2px solid var(--accent)' : '1px solid var(--border-2)',
                  background: format === f.id ? 'rgba(124,110,245,0.08)' : 'none',
                  color: format === f.id ? 'var(--accent)' : 'var(--text-1)', cursor: 'pointer',
                }}>{f.label}</button>
              ))}
            </div>
          </div>

          {/* Type */}
          <div>
            <label style={labelStyle}>Insert As</label>
            <div style={{ display: 'flex', gap: 6 }}>
              {TYPES.map(t => (
                <button key={t.id} onClick={() => setType(t.id)} style={{
                  flex: 1, padding: '8px', borderRadius: 6, fontSize: 10, fontFamily: 'var(--font-mono)', textAlign: 'center',
                  border: type === t.id ? '2px solid var(--accent-2)' : '1px solid var(--border-2)',
                  background: type === t.id ? 'rgba(95,212,164,0.08)' : 'none',
                  color: type === t.id ? 'var(--accent-2)' : 'var(--text-2)', cursor: 'pointer',
                }}>
                  <div style={{ fontWeight: 700 }}>{t.label}</div>
                  <div style={{ fontSize: 8, marginTop: 2 }}>{t.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* File name */}
          <div>
            <label style={labelStyle}>File Name</label>
            <input value={fileName} onChange={e => setFileName(e.target.value)} style={inputStyle} />
          </div>

          {/* Target folder */}
          <div>
            <label style={labelStyle}>Target Folder (optional)</label>
            <input value={targetFolder} onChange={e => setTargetFolder(e.target.value)} placeholder="src/components" style={inputStyle} />
          </div>

          {/* Result */}
          {result && (
            <div style={{ padding: 10, borderRadius: 6, border: `1px solid ${result.ok ? 'var(--accent-2)' : 'var(--red)'}`, background: result.ok ? 'rgba(95,212,164,0.08)' : 'rgba(240,112,112,0.08)' }}>
              {result.ok ? (
                <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--accent-2)' }}>✓ Created: {result.path}</span>
              ) : (
                <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--red)' }}>✗ {result.error}</span>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid var(--border-2)', background: 'none', color: 'var(--text-2)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 11 }}>Cancel</button>
          <button onClick={convert} disabled={converting || (!html && !selectedDesign)} style={{
            flex: 1, padding: 10, borderRadius: 6, border: 'none',
            background: 'var(--accent)', color: '#fff', cursor: 'pointer',
            fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700,
            opacity: converting || (!html && !selectedDesign) ? 0.5 : 1,
          }}>
            {converting ? 'Converting...' : '⚡ Convert & Add'}
          </button>
        </div>
      </div>
    </div>
  )
}

// Small component for saving a design
export function SaveDesignModal({ html, onClose, onSaved }: { html: string; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)

  const save = async () => {
    if (!name.trim()) return
    setSaving(true)
    try {
      await authFetch(apiUrl('/api/designs'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, html }),
      })
      onSaved()
      onClose()
    } catch {} finally { setSaving(false) }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1001, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: 360, background: 'var(--bg-1)', border: '1px solid var(--border-2)', borderRadius: 14, padding: 20, boxShadow: '0 16px 48px rgba(0,0,0,0.5)' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, color: 'var(--text-0)', marginBottom: 12 }}>💾 Save Design</div>
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Design Name</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. IT Support Form" style={inputStyle} autoFocus onKeyDown={e => { if (e.key === 'Enter') save() }} />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 8, borderRadius: 6, border: '1px solid var(--border-2)', background: 'none', color: 'var(--text-2)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 11 }}>Cancel</button>
          <button onClick={save} disabled={saving || !name.trim()} style={{ flex: 1, padding: 8, borderRadius: 6, border: 'none', background: 'var(--accent-2)', color: '#fff', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700 }}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

const inputStyle: React.CSSProperties = { width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border-2)', background: 'var(--bg-2)', color: 'var(--text-0)', fontFamily: 'var(--font-mono)', fontSize: 11, outline: 'none' }
const labelStyle: React.CSSProperties = { display: 'block', fontSize: 9, fontWeight: 700, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginBottom: 4, letterSpacing: '0.05em', textTransform: 'uppercase' }
